<template>
  <div
    v-show="selection"
  >
    <div class="c-page-header">
      <h1 class="c-page-header-title">範囲選択</h1>
    </div>

    <confirm-red-modal
      :isOpen="isOpenBackToHomeworkAddConfirmModal"
      okLabel="宿題作成に戻る"
      @ok="cancel"
      @cancel="closeBackToHomeworkAddConfirmModal"
    >
      <template v-slot:text> 宿題作成に戻りますか？</template>
      <template v-slot:alertText>保存していない範囲選択はリセットされます。</template>
    </confirm-red-modal>

    <section
      v-if="work"
      id="scope-options"
      class="c-parts-box -white p-homework-contents"
    >
      <form
        id="search-area"
        class="p-homework-page-search"
      >
        <div class="p-homework-page-search__textbook">選択教材：<span class="text-name">{{ work.name }}</span></div>
      </form>

      <section
        v-for="part in work.parts"
        :key="`part-${part.id}`"
        class="p-homework-selection"
      >
        <!-- part -->
        <details
          :id="`part-layer-${part.id}`"
          class="p-homework-selection__details"
          open
        >
          <summary
            v-show="part.title.trim()"
            class="p-homework-selection__summary level1"
          >
            <label :for="`part-${part.id}`">
              <div class="p-homework-selection-checkbox-area">
                <input
                  type="checkbox"
                  :id="`part-${part.id}`"
                  :value="part.id"
                  @change="switchChecked($event)"
                  class="p-homework-selection-checkbox"
                >
                <span class="c-parts-chapter">{{ part.index_label }}</span>
                <span class="p-homework-selection-title">
                  <plain-text-view
                    :markdown-text="part.title"
                    class="u-text-ellipsis"
                  ></plain-text-view>
                </span>
              </div>
            </label>
            <div v-if="part.textbook_page_first">
              <span class="c-parts-text-page">P {{part.textbook_page_first}}-{{part.textbook_page_last}}</span>
            </div>
          </summary>

          <!-- chapter -->
          <details
            v-for="chapter in part.chapters"
            :key="`chapter-${chapter.id}`"
            :id="`chapter-layer-${chapter.id}`"
            class="p-homework-selection__details"
            open
          >
            <summary class="p-homework-selection__summary level2">
              <label :for="`chapter-${chapter.id}`">
                <div class="p-homework-selection-checkbox-area">
                  <input
                    type="checkbox"
                    :id="`chapter-${chapter.id}`"
                    :value="chapter.id"
                    @change="switchChecked($event)"
                    class="p-homework-selection-checkbox"
                  >
                  <span
                    v-if="chapter.index_label"
                    class="c-parts-chapter"
                  >{{ chapter.index_label }}</span>
                  <span class="p-homework-selection-title">
                    <plain-text-view
                      :markdown-text="chapter.title"
                      class="u-text-ellipsis"
                    ></plain-text-view>
                  </span>
                </div>
              </label>
              <div v-if="chapter.textbook_page_first">
                <span class="c-parts-text-page">P {{chapter.textbook_page_first}}-{{chapter.textbook_page_last}}</span>
              </div>
            </summary>

            <!-- section -->
            <details
              v-for="section in chapter.sections"
              :key="`section-${section.id}`"
              :id="`section-layer-${section.id}`"
              class="p-homework-selection__details"
              open
            >
              <summary class="p-homework-selection__summary level3">
                <label :for="`section-${section.id}`">
                  <div class="p-homework-selection-checkbox-area">
                    <input
                      type="checkbox"
                      :id="`section-${section.id}`"
                      :value="section.id"
                      @change="switchChecked($event)"
                      class="p-homework-selection-checkbox"
                    >
                    <span class="c-parts-chapter">{{ section.sort_order }}</span>
                    <span class="p-homework-selection-title">
                      <plain-text-view
                        :markdown-text="section.title"
                        class="u-text-ellipsis"
                      ></plain-text-view>
                    </span>
                  </div>
                </label>
              <div v-if="section.textbook_page_first">
                <span class="c-parts-text-page">P {{section.textbook_page_first}}-{{section.textbook_page_last}}</span>
              </div>
              </summary>
              <!-- question_set -->
              <div
                v-for="question_set in section.question_sets"
                :key="`question_set-${question_set.id}`"
                :class="{'p-homework-selection-question level4': true, '-selected': selectedQuestionSetIds.includes(question_set.id)}"
              >
                <div class="p-homework-selection-question__text">
                  <label
                    :for="`question_set-${question_set.id}`"
                    class="c-form-checkbox-area"
                  >
                    <input
                      type="checkbox"
                      :id="`question_set-${question_set.id}`"
                      :value="question_set.id"
                      :checked="selectedQuestionSetIds.includes(question_set.id)"
                      @change="switchChecked($event)"
                      class="c-form-checkbox"
                    >
                    <span class="c-form-checkbox-label -select">
                      <span class="p-homework-selection-title">
                        <span
                          v-if="[1, 2].includes(question_set.question_set_label_id)"
                          :class="{'c-parts-question': true, '-application': question_set.question_set_label_id === 2 }"
                        >{{ question_set.question_set_label_name }}</span>
                        <plain-text-view
                          :markdown-text="question_set.title"
                          class="u-text-ellipsis"
                        ></plain-text-view>
                      </span>
                    </span>
                  </label>
                  <div v-if="question_set.textbook_page_range">
                    <span class="c-parts-text-page">P {{question_set.textbook_page_range}}</span>
                  </div>
                </div>
                <div class="p-homework-selection-question__button">
                  <a
                    :href="openAnswerPreview(question_set.answer_preview_url, question_set.id)"
                    class="c-button-white -problem -light-shadow"
                    target="_blank"
                    >問題を確認
                  </a>
                </div>
              </div>
            </details>
          </details>
        </details>
      </section>
    </section>
    <section class="p-homework-select-problem-number">
      <a
        href="javascript:void(0);"
        class="c-button-white -return"
        @click="openBackToHomeworkAddConfirmModal()"
      >
        <span class="label">宿題作成に戻る</span>
      </a>
      <ul class="p-homework-select-problem-number-list">
        <li>
          <span class="title">選択問題数：</span><span class="problem -multi">基本問題</span><span class="number">{{
            sumOfQuestionSetLabels.basic
          }}</span>
        </li>
        <li>
          <span class="problem -multi">応用問題</span><span class="number">{{ sumOfQuestionSetLabels.advanced }}</span>
        </li>
        <li>
          <span class="problem -multi">その他</span><span class="number">{{ sumOfQuestionSetLabels.other }}</span>
        </li>
        <li>
          <span class="problem">合計</span><span class="number">{{ sumOfQuestionSetLabels.basic + sumOfQuestionSetLabels.advanced + sumOfQuestionSetLabels.other }}</span>
        </li>
      </ul>
      <button
        type="button"
        name="button"
        :class="{'c-button-blue -small-no-icon': true, '-disabled': selectedQuestionSetIds.length === 0}"
        @click="$emit('fix')"
      >
        <span class="label">出題範囲を決定する</span>
      </button>
    </section>
  </div>
</template>
<script setup lang="ts">
  import { ref, computed, watch, onMounted } from "vue";

  const props = withDefaults(
    defineProps<{
      selectedQuestionSetIds: Array<number>;
      isShow?: boolean;
      work: any
    }>(),
    {
      isShow: false
    }
  )

  const emit = defineEmits<{
    (e: 'update:selectedQuestionSetIds', value: Array<number>): void;
    (e: 'cancel'): void; 
    (e: 'fix'): void; 
  }>()

  const levels = ref(["part", "chapter", "section", "question_set"])
  const isOpenBackToHomeworkAddConfirmModal = ref(false)
  const originalY = ref("")

  const selection = computed(() => {
    if (props.isShow) {
      scrollToTop()
      window.addEventListener("scroll", fixSearchArea, false)
    } else {
      scrollToOriginal()
      window.removeEventListener("scroll", fixSearchArea, false)
    }
    return props.isShow
  })

  watch(
    () => props.isShow,
    () => {
    if (props.isShow) {
      for (const questionSetId of props.selectedQuestionSetIds) {
        // 選択済みの出題単位idsの上位階層チェックボックスをONにする
        switchUpperLayer(`question_set-${questionSetId}`)
      }
    } else {
      let checkboxes: Array<HTMLInputElement> = Array.from(
          document.querySelectorAll("#scope-options input[type='checkbox']")
      )
      checkboxes.forEach((checkbox) => {
        if (!checkbox.id.startsWith("question_set")) {
          // 出題単位より上位階層のチェックボックスを全てOFFにする。
          // 出題単位は v-model で管理しているので、
          // 出題単位のチェックON/OFFは v-model 値変更時のみ変更されるべき。
          checkbox.checked = false
        }
      })
    }
  })


  const sumOfQuestionSetLabels = computed((): { basic: number, advanced: number, other: number } => {
    let basic = 0
    let advanced = 0
    let other = 0

    if (props.work) {
      for (const part of props.work.parts) {
        for (const chapter of part.chapters) {
          for (const section of chapter.sections) {
            for (const question_set of section.question_sets) {
              if (props.selectedQuestionSetIds.includes(question_set.id)) {
                if (question_set.question_set_label_id === 1) {
                  basic += 1
                } else if (question_set.question_set_label_id === 2) {
                  advanced += 1
                } else {
                  other += 1
                }
              }
            }
          }
        }
      }
    }
    return { basic: basic, advanced: advanced, other: other }
  })

  onMounted(() => {
    window.addEventListener("scroll", () => {
        // show時の縦方向のスクロール位置保持しておく
        document.documentElement.style.setProperty("--scroll-y-origin", `${window.scrollY}px`)
      }
    )
  })

  function scrollToTop() {
    // 範囲選択画面を表示した際のスクロール位置をとっておく
    const scrollY = document.documentElement.style.getPropertyValue("--scroll-y-origin")
    originalY.value = `-${scrollY}`
    // 範囲選択画面はページ先頭から表示
    window.scrollTo(0, 0)
  }

  function scrollToOriginal() {
    // スクロール位置を元に戻す
    window.scrollTo(0, parseInt(originalY.value || "0") * -1)
  }

  function fixSearchArea() {
    const searchArea = document.getElementById("search-area")
    const winScroll = window.scrollY;
    if (winScroll > 152) {
      // スクロール後の「範囲選択」エリア
      searchArea.classList.add("-fixed");
    } else {
      // 初期表示時の「範囲選択」エリア
      searchArea.classList.remove("-fixed");
    }
  }

  function switchLowerLayer(targetId: string) {
    // 下位階層: ON/OFF連動 + 出題単位ids更新

    const ownLevel = targetId.split("-")[0]  // e.g. chapter-123 => chapter
    const ownLevelIndex = levels.value.findIndex((position) => position === ownLevel)
    const ownCheckBox: HTMLInputElement = document.getElementById(targetId) as HTMLInputElement
    const ownLayer = ownCheckBox.closest("details")

    // 下位階層: ON/OFF
    let questionSetIds = []
    if (ownLevelIndex === levels.value.length - 1) {
      // 自身が最下層 (出題単位) の場合、value を追加するのみ
      questionSetIds.push(parseInt(ownCheckBox.value))
    } else {
      // 自分配下の checkbox たち (自身も含む) をすべて ON/OFF する
      let ownLayerCheckBoxes: Array<HTMLInputElement> = Array.from(ownLayer.querySelectorAll("input[type='checkbox']"))
      for (const cbx of ownLayerCheckBoxes) {
        cbx.checked = ownCheckBox.checked

        if (cbx.id.startsWith("question_set")) {
          questionSetIds.push(parseInt(cbx.value))
        }
      }
    }

    // 出題単位ids更新
    // FIXME: 別メソッドでも良いかも
    if (ownCheckBox.checked) {
      // ON時: 出題単位idsを追加する (重複は除去)
      emit("update:selectedQuestionSetIds", [...new Set(props.selectedQuestionSetIds.concat(questionSetIds))])
    } else {
      // OFF時: 出題単位idsを削除する
      let selectedQuestionSetIdsSet = new Set(props.selectedQuestionSetIds)
      for (const qsId of questionSetIds) {
        selectedQuestionSetIdsSet.delete(qsId)
      }
      emit("update:selectedQuestionSetIds", [...selectedQuestionSetIdsSet])
    }
  }

  function switchUpperLayer(targetId: string) {
    // 上位階層: ON/OFF連動

    const ownLevel = targetId.split("-")[0]  // e.g. chapter-123 => chapter
    const ownLevelIndex = levels.value.findIndex((position) => position === ownLevel)
    const ownCheckBox: HTMLInputElement = document.getElementById(targetId) as HTMLInputElement
    const ownLayer = ownCheckBox.closest("details")

    const parentLevelIndex = ownLevelIndex - 1
    if (parentLevelIndex < 0) {
      // 親がいない場合は終了
      return
    }

    const parentLevel = levels.value[parentLevelIndex]
    const parentLayer = ownLayer.closest(`details[id^=${parentLevel}]`)
    let parentCheckBox: HTMLInputElement = parentLayer.querySelector(`input[type='checkbox'][id^=${parentLevel}]`)

    if (ownCheckBox.checked) {
      // ON時: 兄弟がすべて ON の場合は、自分の親 checkbox を ON
      // 同一親配下の自階層 checkbox たち (兄弟)
      const brotherCheckboxes: Array<HTMLInputElement> = Array.from(parentLayer.querySelectorAll(
        `input[type='checkbox'][id^=${ownLevel}]`
      ))
      const brotherCheckedList = [...brotherCheckboxes].map(brother => brother.checked)
      if (brotherCheckedList.every((checked) => checked === true)) {
        parentCheckBox.checked = ownCheckBox.checked
      }
    } else {
      // OFF時: 自分の親 checkbox を OFF
      parentCheckBox.checked = ownCheckBox.checked
    }
    // さらに上位項目に遡って適用する
    switchUpperLayer(parentCheckBox.id)
  }

  function switchChecked(event) {
    const targetId = event.target.id

    // 下位階層: ON/OFF連動 + 出題単位ids更新
    switchLowerLayer(targetId)
    // 上位階層: ON/OFF連動
    switchUpperLayer(targetId)
  }

  function cancel() {
    emit("cancel")
    // スクロール一位置を保証するため、BaseModal.unfixBackground() が終わってから
    // scrollToOriginal() が実行されるようにする
    closeBackToHomeworkAddConfirmModal()
  }

  function openBackToHomeworkAddConfirmModal() {
    isOpenBackToHomeworkAddConfirmModal.value = true
  }

  function closeBackToHomeworkAddConfirmModal() {
    isOpenBackToHomeworkAddConfirmModal.value = false
  }

  // 「問題を確認」ボタン押下時
  function openAnswerPreview(answer_preview_url: string, question_set_id: number) {
    return answer_preview_url + "?is_homework=true&question_set=" + question_set_id
  }
</script>
